import React from 'react';
import { motion } from 'framer-motion';

const FOMOShoppingLandingPage = ({ goToInput, theme = "mint" }) => {
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.3 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* SEO-optimized heading structure */}
      <h1 className="text-3xl font-bold text-center mb-6 dark:text-white">
        FOMO Shopping Assessment: Measure Your Fear-of-Missing-Out Purchases
      </h1>
      
      {/* Hero section with main CTA */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-6 mb-8">
        <div className="flex flex-col md:flex-row items-center">
          <div className="mb-6 md:mb-0 md:mr-6 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-3 dark:text-white">Calculate Your FOMO Shopping Score</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Track your fear-driven purchases, understand your FOMO triggers, and break free from the anxiety of missing out on deals and trends.
            </p>
            <motion.button
              onClick={goToInput}
              className={`px-6 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Track Your FOMO Purchase
            </motion.button>
          </div>
          <div className="text-8xl">👀</div>
        </div>
      </div>
      
      {/* What causes FOMO shopping section */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Why FOMO Drives Our Shopping Decisions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">⏱️</span>
              <div>
                <h3 className="font-bold dark:text-white">Limited-Time Offers</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Countdown timers and "ending soon" messaging create artificial urgency that bypasses rational decision-making.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">📱</span>
              <div>
                <h3 className="font-bold dark:text-white">Social Media Influence</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Seeing others with products creates a comparative anxiety and desire to keep up with trends.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🏆</span>
              <div>
                <h3 className="font-bold dark:text-white">Exclusivity & Scarcity</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  "Limited edition" and "while supplies last" messaging taps into our fear of forever missing an opportunity.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🧠</span>
              <div>
                <h3 className="font-bold dark:text-white">Anticipated Regret</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  The fear of future disappointment ("I'll regret not buying this") drives immediate purchases.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>
      
      {/* FOMO Score Calculation section */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Our FOMO Shopping Assessment Works</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">1</span>
              Identify FOMO Triggers
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Track the specific urgency tactics and social pressures that lead to your fear-based purchases.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">2</span>
              Calculate Your FOMO Score
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Our algorithm analyzes how strongly fear of missing out influenced your purchase decision.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">3</span>
              Receive Personalized Strategies
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Get tailored advice to overcome your specific FOMO shopping patterns and marketing vulnerabilities.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">4</span>
              Track Progress Over Time
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Monitor how your fear-based shopping decreases as you implement mindfulness techniques and resistance strategies.
            </p>
          </div>
        </div>
      </motion.section>
      
      {/* FOMO Shopper Types */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Discover Your FOMO Shopper Type</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🕒</span>
              <h3 className="font-bold mt-2 dark:text-white">The Deadline Dasher</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You're highly susceptible to limited-time offers and countdown timers, often making rushed decisions to "beat the clock."
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">👥</span>
              <h3 className="font-bold mt-2 dark:text-white">The Social Comparator</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You purchase items after seeing friends, influencers, or peers with them, fearing social exclusion if you don't keep up.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🎯</span>
              <h3 className="font-bold mt-2 dark:text-white">The Collector</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You're drawn to limited editions and exclusive items, feeling compelled to complete collections or own special releases.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🔍</span>
              <h3 className="font-bold mt-2 dark:text-white">The Opportunity Maximizer</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You're haunted by potential future regret, often buying "just in case" to avoid missing what might be a good opportunity.
            </p>
          </div>
        </div>
      </motion.section>
      
      {/* FOMO Shopping Statistics */}
      <motion.section 
        className="mb-8 bg-gray-50 dark:bg-gray-700 rounded-xl p-5"
        variants={itemVariants}
      >
        <h2 className="text-xl font-bold mb-4 dark:text-white">FOMO Shopping By The Numbers</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold dark:text-white">60%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">of millennials make reactive purchases due to FOMO</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">48%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">higher spending when items are labeled "limited edition"</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">39%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">of shoppers experience higher regret with FOMO purchases</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">3.4x</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">more likely to buy when countdown timers are present</p>
          </div>
        </div>
      </motion.section>
      
      {/* FOMO Marketing Tactics Awareness */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Common FOMO Marketing Tactics to Recognize</h2>
        
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">⏳</span>
              <div>
                <h3 className="font-bold dark:text-white">Countdown Timers</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Creating artificial urgency with visual clocks counting down to the end of sales or availability.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">👁️</span>
              <div>
                <h3 className="font-bold dark:text-white">Real-Time Activity Notifications</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  "10 other people are viewing this right now" or "5 purchased in the last hour" creates competitive pressure.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">📊</span>
              <div>
                <h3 className="font-bold dark:text-white">Low Stock Warnings</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  "Only 2 left!" messages that trigger fear of missing out on items that might not be restocked.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🔔</span>
              <div>
                <h3 className="font-bold dark:text-white">Flash Sales & Limited Drops</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Sudden, short-window sales or product releases designed to create panic buying and immediate action.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>
      
      {/* Testimonials section */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Others Overcame FOMO Shopping</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "Once I realized how much I was spending on 'limited time offers' that always seemed to come back around, I started implementing a 48-hour waiting period. My FOMO spending dropped by 70%."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Jordan K.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Recovered Deadline Dasher</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "I used to buy everything I saw influencers post about, fearing I'd miss the next big trend. Tracking these purchases showed me how rarely I actually used these 'must-have' items."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Leila M.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Former Social Comparator</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>
      
      {/* FAQ Section - important for SEO */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Common Questions About FOMO Shopping</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Why is FOMO shopping so powerful?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              FOMO shopping leverages our psychological fear of regret and social exclusion. From an evolutionary perspective, humans are wired to avoid missing opportunities or being left out of the group. Marketers exploit this survival instinct to create artificial scarcity and urgency that bypasses logical decision-making.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How can I tell if I'm making a FOMO purchase?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Look for signs like feeling anxious about missing out, making rushed decisions, concern about what others will think if you don't have an item, or focusing more on the potential of missing the opportunity than on the actual value of the product. Our assessment helps identify these patterns.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Are limited-time offers really as limited as they claim?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Rarely. Many "limited-time" offers are cyclical or have extended deadlines. Flash sales, seasonal promotions, and countdown timers are often marketing tactics rather than genuine scarcity. Our assessment helps you distinguish between authentic limited availability and manufactured urgency.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How can I make better decisions when feeling FOMO?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Implement a waiting period (24-48 hours) for non-essential purchases, create a wish list instead of immediately buying, block or limit social media during vulnerable times, and use our tool to track and understand your FOMO triggers. The more aware you are of these pressures, the better you can resist them.
            </p>
          </div>
        </div>
      </motion.section>
      
      {/* FOMO Resistance Strategies */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">FOMO-Proof Your Shopping Habits</h2>
        
        <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 space-y-4">
          <div className="flex items-start">
            <span className="text-2xl mr-3">⏰</span>
            <div>
              <h3 className="font-bold dark:text-white">The 48-Hour Rule</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Wait 48 hours before purchasing any non-essential item. If it's a "limited" offer, research shows it will likely return.
              </p>
            </div>
          </div>
          
          <div className="flex items-start">
            <span className="text-2xl mr-3">📵</span>
            <div>
              <h3 className="font-bold dark:text-white">Digital Detox Periods</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Schedule regular breaks from social media and shopping apps to reset your comparison triggers.
              </p>
            </div>
          </div>
          
          <div className="flex items-start">
            <span className="text-2xl mr-3">📝</span>
            <div>
              <h3 className="font-bold dark:text-white">The FOMO Journal</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Track items you wanted but didn't buy due to FOMO. Check back later to see if you still want them (most people don't).
              </p>
            </div>
          </div>
          
          <div className="flex items-start">
            <span className="text-2xl mr-3">🧩</span>
            <div>
              <h3 className="font-bold dark:text-white">Need vs. Want Analysis</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                For each potential purchase, ask: "Would I want this if no one else knew I had it?" and "Would I want this if it wasn't on sale?"
              </p>
            </div>
          </div>
        </div>
      </motion.section>
      
      {/* CTA section */}
      <motion.section 
        className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-xl p-6 text-center"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-3 dark:text-white">Ready to Break Free from FOMO Shopping?</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          Start tracking your fear-of-missing-out purchases and develop immunity to urgency marketing.
        </p>
        <motion.button
          onClick={goToInput}
          className={`px-8 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Calculate Your FOMO Score Now
        </motion.button>
      </motion.section>
      
      {/* Rich SEO footer content */}
      <footer className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">About FOMO Shopping</h2>
          <p className="mb-3">
            FOMO (Fear of Missing Out) shopping refers to purchases driven by anxiety about missing deals, opportunities, or experiences. This increasingly common phenomenon is fueled by social media, limited-time marketing tactics, and our natural aversion to potential regret.
          </p>
          <p className="mb-3">
            Our FOMO Shopping Assessment helps you understand and address these feelings by identifying your specific fear triggers, analyzing your vulnerability to different marketing tactics, and providing personalized strategies to make more intentional purchasing decisions.
          </p>
          <p>
            Whether you're prone to panic-buying during flash sales, purchasing items after seeing them on social media, or collecting limited editions you don't need, our free tool helps you develop resilience to FOMO marketing and reduce fear-based shopping behaviors.
          </p>
        </div>
      </footer>
    </motion.div>
  );
};

export default FOMOShoppingLandingPage;
